<template>
    <div>
        <b-modal :visible="true"
                 size="xl"
                 :title="`${item.extendedProps.Name}, ${format(new Date(item.start), 'HH:mm')} - ${format(new Date(item.originalEnd || item.end), 'HH:mm')}`"
                 @hidden="$emit('close')">
            <div v-if="error"
                 class="alert alert-danger">
                {{ error }}
            </div>

            <div class="w-100"
                 style="max-width: 550px">
                <dl class="row">
                    <dt class="col-5">
                        {{ $t('common.when') }}:
                    </dt>
                    <dd class="col-7">
                        {{ format(new Date(item.start), 'EEEE d MMMM HH:mm', { locale: locales[i18next.language] || locales['en-GB'] }) }} - {{ format(new Date(item.originalEnd || item.end), 'HH:mm') }}
                    </dd>
                    <dt class="col-5">
                        {{ $t('common.warehouse') }}:
                    </dt>
                    <dd class="col-7">
                        {{ item.extendedProps['Warehouse.Name'] }}
                    </dd>

                    <dt class="col-5">
                        {{ $t('common.location') }}:
                    </dt>
                    <dd class="col-7">
                        {{ item.extendedProps['Location.Name'] }}
                    </dd>

                    <dt v-if="item.extendedProps.occupation"
                        class="col-5">
                        {{ $t('common.occupation') }}:
                    </dt>
                    <dd v-if="item.extendedProps.occupation"
                        class="col-7 card-text">
                        <span v-if="item.extendedProps.occupation">
                            <span data-rel="occupation">{{ item.extendedProps.CurrentEntryQuantity || 0 }}</span> / {{ item.extendedProps.MaxEntryQuantity }}
                        </span>
                        <span v-if="isPickingTimeInQueue"
                              class="ml-2 badge badge-warning">
                            {{ $t('reservation.queue') }}<span v-if="!isNull(queueCount) && queueCount > 0"
                                                               class="ml-1 badge badge-light">{{ queueCount }}</span>
                        </span>
                    </dd>
                </dl>
                <div v-if="!isNull(queueCount)"
                     class="alert alert-warning w-100"
                     role="alert">
                    {{ $t('reservation.full_moment_queue') }}
                </div>
            </div>

            <div class="row">
                <div class="col mb-3">
                    <hr>
                </div>
            </div>

            <!-- Loading state -->
            <div v-if="isInitialLoading"
                 class="text-center py-5">
                <b-spinner variant="primary" />
                <div class="mt-2">
                    {{ $t('common.loading') }}
                </div>
            </div>

            <template v-else>
                <!-- Login prompt for non-logged in users -->
                <div class="row">
                    <div class="col col-lg-6">
                        <div class="row">
                            <div class="col col-12">
                                <h5>{{ $t('reservation.reserve_with_subscription') }}</h5>
                            </div>
                        </div>
                        <div v-if="isLoggedIn">
                            <b-list-group v-if="subscriptions.length">
                                <b-list-group-item v-for="subscription in subscriptions"
                                                   :key="subscription.id"
                                                   :button="true"
                                                   :disabled="isActionLoading"
                                                   class="d-flex justify-content-between align-items-center"
                                                   @click="reserveWithSubscription(subscription)">
                                    <div>
                                        <strong>{{ subscription.name }}</strong>
                                        <small v-if="subscription.remainingUses !== null">
                                            ({{ subscription.remainingUses }} {{ $t('reservation.uses_remaining') }})
                                        </small>
                                        <div v-if="subscription.dateRange && subscription.dateRange?.start && subscription.dateRange?.end"
                                             class="text-success">
                                            {{ $t('common.validity') }}:&nbsp;{{ format(new Date(subscription.dateRange.start), 'dd-MM-yyyy') }} - {{ format(new Date(subscription.dateRange.end), 'dd-MM-yyyy') }}
                                        </div>
                                    </div>
                                    <b-button :variant="isPickingTimeInQueue ? 'outline-dark' : 'outline-success'"
                                              size="sm"
                                              :disabled="isActionLoading">
                                        <b-spinner v-if="isActionLoading"
                                                   small />
                                        <span v-else>{{ isPickingTimeInQueue ? $t('actions.join_queue') : $t('actions.reserve') }}</span>
                                    </b-button>
                                </b-list-group-item>
                            </b-list-group>
                            <div v-else
                                 class="alert alert-info"
                                 style="min-height: 110px;">
                                {{ $t('reservation.no_valid_subscriptions') }}
                                <div v-if="slug"
                                     class="mt-3">
                                    <a :href="slug"
                                       class="btn btn-primary">
                                        {{ $t('reservation.view_available_subscriptions') }}
                                    </a>
                                </div>
                            </div>

                            <div v-if="isLoggedIn && subscriptions.length && slug"
                                 class="text-center">
                                <a :href="slug"
                                   class="btn btn-outline-primary mt-3">
                                    {{ $t('reservation.view_available_subscriptions') }}
                                </a>
                            </div>
                        </div>

                        <div v-else>
                            <div class="alert alert-dark text-center pt-4"
                                 style="min-height: 110px;">
                                {{ $t('reservation.have_product_to_reserve') }}
                                <a :href="loginUrl"
                                   class="alert-link text-nowrap">
                                    {{ $t('common.click_to_login') }}
                                    <font-awesome-icon :icon="icons.faLock"
                                                       size="sm"
                                                       fixed-width />
                                </a>
                            </div>
                        </div>
                    </div>

                    <!-- Products section (for all users) -->
                    <div class="col col-lg-6">
                        <h5>{{ $t('reservation.buy_ticket_for_time') }}</h5>
                        <b-list-group v-if="products.length">
                            <b-list-group-item v-for="product in products"
                                               :key="product.id"
                                               :button="true"
                                               :disabled="isActionLoading"
                                               class="d-flex justify-content-between align-items-center"
                                               @click="addToCart(product)">
                                <div>
                                    {{ product.name }}
                                    <div class="small text-muted">
                                        {{ product.price }}
                                    </div>
                                </div>

                                <b-button variant="outline-primary"
                                          size="sm"
                                          :disabled="isActionLoading">
                                    <b-spinner v-if="isActionLoading"
                                               small />
                                    <span v-else>{{ $t('actions.add_to_cart') }}</span>
                                </b-button>
                            </b-list-group-item>
                        </b-list-group>
                        <div v-else
                             class="alert alert-info"
                             style="min-height: 110px;">
                            {{ $t('reservation.no_products_available') }}
                            <div class="mt-3">
                                <a v-if="slug"
                                   :href="slug"
                                   class="btn btn-primary">
                                    {{ $t('reservation.view_available_products') }}
                                </a>
                                <a href="/contact"
                                   class="btn btn-link">
                                    {{ $t('reservation.contact_us') }}
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </template>

            <template #modal-footer>
                <div class="d-flex justify-content-between w-100">
                    <div>
                        <b-button v-if="hasSiblings"
                                  variant="outline-secondary"
                                  @click="$emit('show-siblings')">
                            {{ $t('reservation.show_timestamps') }}
                        </b-button>
                    </div>
                    <div>
                        <b-button variant="outline-secondary"
                                  :disabled="isActionLoading"
                                  @click="$emit('close')">
                            {{ $t('actions.cancel') }}
                        </b-button>
                    </div>
                </div>
            </template>
        </b-modal>

        <add-to-cart-modal v-if="showAddedToCartModal"
                           :show-incasso-warning="hasIncassoProcess"
                           @close="closeAddedToCartModal" />
    </div>
</template>

<script>
import { format } from 'date-fns'
import axios from 'axios'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faLock } from '@fortawesome/pro-solid-svg-icons'
import AddToCartModal from '../cart/AddToCartModal.vue'
import { cartEventBus } from '../cart'
import { isNull } from 'lodash'
import i18next from 'i18next'
import nl from 'date-fns/locale/nl'
import enGB from 'date-fns/locale/en-GB'

export default {
  name: 'ReservationModal',

  components: {
    FontAwesomeIcon,
    AddToCartModal
  },

  props: {
    item: {
      type: Object,
      required: true
    },
    csrfToken: {
      type: String,
      required: true
    },
    hasSiblings: {
      type: [Boolean, Object],
      default: false
    }
  },

  data () {
    return {
      isInitialLoading: true,
      isActionLoading: false,
      subscriptions: [],
      products: [],
      slug: null,
      format,
      isNull,
      error: null,
      icons: {
        faLock
      },
      i18next,
      locales: {
        nl,
        en: enGB
      },
      showAddedToCartModal: false,
      hasIncassoProcess: false
    }
  },

  computed: {
    isLoggedIn () {
      return window.LOGGED_IN
    },

    getWindow () {
      return window.csrf_token
    },

    loginUrl () {
      const currentUrl = window.location.href
      return (window.loginUrl || '/inloggen') + `?referer=${encodeURIComponent(currentUrl)}`
    },

    isPickingTimeInQueue () {
      return (parseInt(this.item.extendedProps.CurrentEntryQuantity) >= parseInt(this.item.extendedProps.MaxEntryQuantity)) &&
             this.item.extendedProps.QueueAttendanceYN
    },

    queueCount () {
      return this.isPickingTimeInQueue ? (this.item.extendedProps.QueueQuantity || null) : null
    }
  },

  methods: {
    async addToCart (product) {
      this.isActionLoading = true
      try {
        const response = await axios.post('/php/xhr/ajax.php', {
          API: true,
          csrf_token: this.csrfToken,
          PartID: product.id,
          SubscriptionAgendaID: product?.SubscriptionAgendaID || null,
          noRedirect: true
        }, {
          params: {
            function: 'dataduiker/addToCart'
          }
        })

        if (response.data.error) {
          this.error = response.data.error
          return
        }

        // Trigger the cart modal with incasso process status
        cartEventBus.$emit('show-cart-modal', {
          hasIncassoProcess: response.data?.IncassoProcessYN || false
        })

        this.$emit('close')
      } catch (error) {
        if (error) {
          this.error = error?.response?.data || 'Er is een fout opgetreden bij het toevoegen aan de winkelwagen.'
        }
      } finally {
        this.isActionLoading = false
      }
    },

    async reserveWithSubscription (subscription) {
      this.isActionLoading = true
      this.error = null
      const WorkFlowStatus = this.isPickingTimeInQueue ? 'DRAFT' : 'FINAL'

      try {
        const formData = new URLSearchParams()
        const payload = {
          important: '',
          'form-type': 'ShoppingCartForm',
          timestamp: new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' }),
          csrf_token: this.csrfToken,
          one_click_reservation: '1',
          ParentSubscriptionID: subscription.id,
          PartID: 'PICK_TIME',
          FORM_ID: 'fbc7510158b449501df45bfd779dd8e2', // FORM ID of ticket-modal.php
          FORM_METHOD: 'SelectTimeSlot',
          FIELD_ID: subscription.id,
          Quantity: '1',
          SubscriptionAgendaID: this.item.extendedProps.SubscriptionAgendaID,
          WorkFlowStatus,
          TimeBlockYN: '1',
          ActivityID: this.item.extendedProps.ActivityID
        }

        // Set the submit to reserve direcly or join queue.
        payload[this.isPickingTimeInQueue ? 'SUBMIT_TERTIARY' : 'SUBMIT_SECONDARY'] = ''

        for (const key in payload) {
          formData.append(key, payload[key])
        }

        const response = await axios.post('/php/xhr/ajax.php', formData, {
          params: {
            function: 'dataduiker/addToCart'
          }
        })

        if (response.data.error) {
          this.error = response.data.error
          return
        }

        // Trigger the cart modal with incasso process status
        cartEventBus.$emit('show-cart-modal', {
          hasIncassoProcess: response.data?.IncassoProcessYN || false,
          message_type: response.data.message_type || false
        })

        this.$emit('close')
      } catch (error) {
        if (error) {
          this.error = error?.response?.data || 'Er is een fout opgetreden bij het toevoegen aan de winkelwagen.'
        }
      } finally {
        this.isActionLoading = false
      }
    },

    closeAddedToCartModal () {
      this.showAddedToCartModal = false
      this.hasIncassoProcess = false
      this.$emit('close')
    },

    async fetchReservationOptions () {
      this.isInitialLoading = true
      try {
        const response = await axios.get('/php/xhr/ajax.php', {
          params: {
            function: 'dataduiker/getReservationOptions',
            SubscriptionAgendaID: this.item.extendedProps.SubscriptionAgendaID
          }
        })

        this.subscriptions = response.data.subscriptions || []
        this.products = response.data.products.filter(product => product.Quantity === 1) || []

        this.slug = response.data.slug || null
      } catch (error) {
        console.error('Error loading reservation options:', error) // eslint-disable-line no-console
      } finally {
        this.isInitialLoading = false
      }
    }
  }
}
</script>
